/**
 * @file DPD.jsx
 * @description DPD component for displaying and managing DPD.
 * @component DPD
 * @exports DPD - Main component for displaying and managing DPD.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DPD_TABS } from "../../../common/constants/dashboardModule/dpdConstants.js";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { getDashboardPastdue as getPastdueData } from "../../../services/pbi/pbiservices";
import { fetchDPDConversations } from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import DPDStackedAreaChart from "./DPDchart";

// ── Constants-1 ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const MINT  = "#3DBE92";
const PINK  = "#D4537E";

// Bucket config — single source of truth for column key, label, and color.
// Used to render KPI cards, table columns, and the drill metric chip.
const BUCKETS = [
    { key: "days_1_9",     label: "1–9 Days",   color: MINT  },
    { key: "days_10_19",   label: "10–19 Days", color: GREEN },
    { key: "days_20_29",   label: "20–29 Days", color: GREEN },
    { key: "days_30_39",   label: "30–39 Days", color: BLUE  },
    { key: "days_40_49",   label: "40–49 Days", color: AMBER },
    { key: "days_50_59",   label: "50–59 Days", color: AMBER },
    { key: "days_60_89",   label: "60–89 Days", color: PINK  },
    { key: "days_90_plus", label: "90+ Days",   color: PINK  },
];
const BUCKET_BY_KEY = Object.fromEntries(BUCKETS.map(b => [b.key, b]));

// ── Column config (matches TrendsDashboard pastdue ranges) ─────────
const COLS = [
    { key: "summary_date",  label: "DPD Date",      width: 110, align: "left"  },
    ...BUCKETS.map(b => ({ key: b.key, label: b.label, width: 90, align: "right" })),
    { key: "total_records", label: "Total Records", width: 110, align: "right" },
];

// 40px drill icon column at the start
const TABLE_COLS  = [{ key: "_drill", label: "", width: 40, align: "left" }, ...COLS];
const GRID_COLS   = TABLE_COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 64;
const ROWS_PER_PAGE = 25;

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
    if (!val) return null;
    if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
    // Match YYYY-MM-DD whether or not a time portion follows. This handles
    // both "2026-05-08" and "2026-05-08T00:00:00.000Z" — the latter would
    // otherwise be parsed as UTC midnight and land on the previous day in
    // negative-offset timezones.
    if (typeof val === "string") {
        const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    }
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
}

const formatDate = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return val || "—";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const toLocalDateStr = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
};

const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

// Colour-codes a bucket value relative to the row total, and makes the cell
// clickable when count > 0.
function BucketCell({ value, total, onClick }) {
    const n   = Number(value) || 0;
    const pct = total > 0 ? n / total : 0;
    const color =
        pct === 0   ? "text.disabled" :
        pct >= 0.40 ? PINK  :
        pct >= 0.20 ? AMBER :
        pct >= 0.08 ? BLUE  : GREEN;
    const clickable = n > 0 && !!onClick;
    return (
        <Box
            component="span"
            onClick={clickable ? onClick : undefined}
            sx={{
                color,
                fontWeight: pct > 0 ? 500 : 400,
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { textDecoration: "underline" } : {},
            }}
        >
            {n || "—"}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const DPD = () => {
    const today          = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate,   setEndDate]   = useState(today);
    const [dpdType,   setDpdType]   = useState(DPD_TABS.TEXTS);
    const [dpdData,   setDpdData]   = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState("");
    const [page,      setPage]      = useState(0);
    const [sortKey,   setSortKey]   = useState("summary_date");
    const [sortDir,   setSortDir]   = useState("desc");
    const [drill,     setDrill]     = useState(null);

    useEffect(() => {
        load(firstDayOfMonth, today, DPD_TABS.TEXTS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (start, end, type) => {
        try {
            setLoading(true); setError(""); setPage(0);
            setDpdData((await getPastdueData({
                startDate: toLocalDateStr(start),
                endDate:   toLocalDateStr(end),
                channel:   type === DPD_TABS.EMAILS ? "emails" : "texts",
            })) || []);
        } catch (e) {
            console.error(e);
            setError("Failed to load DPD data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSortCol = (key) => {
        if (key === "_drill") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return dpdData;
        return [...dpdData].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const an = Number(av), bn = Number(bv);
            const cmp = !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [dpdData, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(dpdData.length / ROWS_PER_PAGE);

    const kpis = useMemo(() => {
        const sum = (k) => dpdData.reduce((a, r) => a + (Number(r[k]) || 0), 0);
        const buckets = Object.fromEntries(BUCKETS.map(b => [b.key, sum(b.key)]));
        return { total: sum("total_records"), ...buckets };
    }, [dpdData]);

    // ── Drill-down ────────────────────────────────────────────────
    // bucket only       → that bucket across the picker range
    // date only         → all past-due conversations on that date
    // bucket + date     → that bucket on that single date
    const openDrill = useCallback(({ bucket = null, date = null } = {}) => {
        const bucketCfg = bucket ? BUCKET_BY_KEY[bucket] : null;

        const title =
            bucket && date ? "Past-due conversations" :
            bucket          ? `Past-due: ${bucketCfg.label}` :
                              `Past-due conversations on ${formatDate(date)}`;

        const dStart = date ? new Date(date) : startDate;
        const dEnd   = date ? new Date(date) : endDate;

        // Backend expects "texts" / "emails" the same way the main load does.
        const channel = dpdType === DPD_TABS.EMAILS ? "emails" : "texts";

        setDrill({
            title,
            metric: bucketCfg
                ? { label: "DPD bucket", value: bucketCfg.label, color: bucketCfg.color }
                : null,
            context: [
                date && { label: "Date", value: formatDate(date) },
                !date && startDate && { label: "From", value: formatDate(startDate) },
                !date && endDate   && { label: "To",   value: formatDate(endDate) },
            ].filter(Boolean),
            channel: dpdType,
            fetchData: () => fetchDPDConversations({
                startDate: dStart ? toLocalDateStr(dStart) : null,
                endDate:   dEnd   ? toLocalDateStr(dEnd)   : null,
                dpdType:   channel,
                bucket,  // null → all buckets; otherwise SP filters
            }),
        });
    }, [startDate, endDate, dpdType]);

    // ── CSV export ────────────────────────────────────────────────
    const exportDisabled = loading || sortedData.length === 0;

    const exportCsv = () => {
        if (!sortedData.length) return;
        const headers = COLS.map(c => csvEscape(c.label));
        const rows = sortedData.map(row =>
            COLS.map(col => {
                if (col.key === "summary_date") return csvEscape(formatDate(row.summary_date));
                return csvEscape(row[col.key]);
            }).join(",")
        );
        const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);

        const now = new Date();
        const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
        const channel = String(dpdType).toLowerCase();

        const a = document.createElement("a");
        a.href     = url;
        a.download = `dpd_${channel}_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent label={STRINGS.START_DATE} date={startDate} onDateChange={setStartDate} marginRight={0} />
                <DatePickerComponent label={STRINGS.END_DATE}   date={endDate}
                    onDateChange={d => { if (d >= startDate) setEndDate(d); }} marginRight={0} />
                <Box onClick={() => load(startDate, endDate, dpdType)} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    alignSelf: "flex-end", mb: "1px",
                    "&:hover": { background: "#2a6db5" },
                }}>Search</Box>

                <Box
                    onClick={exportDisabled ? undefined : exportCsv}
                    title={exportDisabled ? "No data to export" : "Export current view to CSV"}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        userSelect: "none",
                        border: "0.5px solid",
                        borderColor: exportDisabled ? "divider" : BLUE,
                        background: "transparent",
                        color: exportDisabled ? "text.disabled" : BLUE,
                        fontWeight: 500,
                        cursor: exportDisabled ? "default" : "pointer",
                        opacity: exportDisabled ? 0.5 : 1,
                        alignSelf: "flex-end", mb: "1px",
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        transition: "all .15s",
                        "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}
                >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                    Export CSV
                </Box>

                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[DPD_TABS.TEXTS, DPD_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => { setDpdType(ch); load(startDate, endDate, ch); }} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  dpdType === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  dpdType === ch ? BLUE : "transparent",
                            color:       dpdType === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>{ch}</Box>
                    ))}
                </Box>
            </Box>

            {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && dpdData.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {STRINGS.NO_RESULTS}
                </Typography>
            )}

            {!loading && dpdData.length > 0 && (
                <>
                    {/* ── KPIs — Total + every bucket is clickable ── */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            {
                                label: "Total Records",
                                value: kpis.total.toLocaleString(),
                                color: BLUE,
                                onClick: kpis.total > 0
                                    ? () => openDrill({}) // all buckets, full range
                                    : undefined,
                            },
                            ...BUCKETS.map(b => ({
                                label: b.label,
                                value: (kpis[b.key] || 0).toLocaleString(),
                                color: b.color,
                                onClick: kpis[b.key] > 0
                                    ? () => openDrill({ bucket: b.key })
                                    : undefined,
                            })),
                        ].map(k => (
                            <Box key={k.label} onClick={k.onClick} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", flex: 1, minWidth: 0,
                                cursor: k.onClick ? "pointer" : "default",
                                transition: "background .15s",
                                "&:hover": k.onClick ? { background: "#efefef" } : {},
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                    {k.label}
                                </Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── Chart ── */}
                    <DPDStackedAreaChart rows={dpdData} title="DPD" />

                    {/* ── Table ── */}
                    <SectionTitle>Detail</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {TABLE_COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: col.key === "_drill" ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": col.key === "_drill" ? {} : { color: BLUE },
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Rows */}
                        {pagedData.map((row, i) => {
                            const total = Number(row.total_records) || 0;
                            return (
                                <Box key={row.summary_date ?? i} sx={{
                                    ...gridSx, py: "7px", fontSize: 12,
                                    borderBottom: "0.5px solid", borderColor: "divider",
                                    background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                    "&:hover": { background: "rgba(55,138,221,0.06)" },
                                    alignItems: "center",
                                }}>
                                    {/* drill icon — all buckets on this date */}
                                    <Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => openDrill({ date: row.summary_date })}
                                            sx={{ width: 24, height: 24 }}
                                            title="View past-due conversations for this date"
                                        >
                                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>

                                    {COLS.map(col => (
                                        <Box key={col.key} sx={{
                                            textAlign: col.align,
                                            display: "flex", alignItems: "center",
                                            justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                            ...(col.key === "summary_date"  && { color: "text.secondary", whiteSpace: "nowrap" }),
                                            ...(col.key === "total_records" && { fontWeight: 600, color: BLUE }),
                                        }}>
                                            {col.key === "summary_date" ? (
                                                formatDate(row.summary_date)
                                            ) : col.key === "total_records" ? (
                                                <Box
                                                    component="span"
                                                    onClick={total > 0
                                                        ? () => openDrill({ date: row.summary_date })
                                                        : undefined}
                                                    sx={{
                                                        cursor: total > 0 ? "pointer" : "default",
                                                        "&:hover": total > 0 ? { textDecoration: "underline" } : {},
                                                    }}
                                                >
                                                    {total.toLocaleString()}
                                                </Box>
                                            ) : (
                                                <BucketCell
                                                    value={row[col.key]}
                                                    total={total}
                                                    onClick={() => openDrill({
                                                        bucket: col.key,
                                                        date:   row.summary_date,
                                                    })}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", mt: 1, px: 1,
                        }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, dpdData.length)} of {dpdData.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Drill-down popup */}
            {drill && (
                <ConversationDrillDownDialog
                    open={!!drill}
                    onClose={() => setDrill(null)}
                    {...drill}
                />
            )}
        </Box>
    );
};

export default DPD;